import { Directive, ElementRef, Input, OnInit } from "@angular/core";
import { ChangeDetectorRef } from "@angular/core";
import {
  Permissions,
  AndPermission,
  OrPermission,
  NotPermission,
  PermissionObject,
} from "../interfaces/acl";

@Directive({
  selector: "[appAccessControl]",
})
export class AccessControlDirective implements OnInit {
  @Input() permission: Permissions = "";
  constructor(private element: ElementRef,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    try {
      // Get resources from localStorage
      const resources = JSON.parse(localStorage.getItem("resources"));

      if (!resources) {
        // If resources are not available, try again after the component renders
        this.cdr.detectChanges();
        return;
      }

      // Parse the permission if it's a string representation of JSON
      let parsedPermission = this.permission;

      if (typeof this.permission === "string") {
        try {
          const finalPermission = this.permission.trim();

          // Check if it's likely a JSON array or object
          if (
            (finalPermission.startsWith("[") &&
              finalPermission.endsWith("]")) ||
            (finalPermission.startsWith("{") && finalPermission.endsWith("}"))
          ) {
            parsedPermission = JSON.parse(finalPermission);
          }
        } catch (e) {
          console.error("Permission parsing failed:", e);
        }
      }

      // Handle the permission checking based on the parsed input
      const canAccess = this.evaluatePermission(parsedPermission, resources);

      if (!canAccess) {
        this.element.nativeElement.remove();
      } 
    } catch (error) {
      this.element.nativeElement.remove();
    }
  }

  private evaluatePermission(
    permission: Permissions,
    resources: string[]
  ): boolean {
    // For empty permissions or other formats, default behavior
    if (!permission || (Array.isArray(permission) && permission.length === 0)) {
      return true;
    }

    /*
     * Handle string input (including || operator)
     */
    if (typeof permission === "string") {
      // Handle OR condition using || operator
      if (permission.includes("||")) {
        const options = permission.split("||").map((p) => p.trim());
        return options.some((option) =>
          this.evaluatePermission(option, resources)
        );
      }

      // Handle negation prefix
      if (permission.startsWith("!")) {
        return !resources.includes(permission.substring(1).trim());
      }

      // Simple string permission
      return resources.includes(permission.trim());
    }

    /*
     * Handle array format - OR logic by default
     */
    if (Array.isArray(permission)) {
      // Check if it's an AND group (array inside array)
      if (permission.length === 1 && Array.isArray(permission[0])) {
        // AND - all permissions required
        return permission[0].every((p) =>
          this.evaluatePermission(p, resources)
        );
      }

      // OR - any permission is sufficient
      return permission.some((p) => this.evaluatePermission(p, resources));
    }

    /*
     * Handle object format for explicit AND/OR/NOT operations using switch/case
     */
    if (typeof permission === "object" && permission !== null) {
      // Determine which permission type we're dealing with
      const permissionType = this.getPermissionType(permission);

      switch (permissionType) {
        case "and": {
          const andPermission = permission as AndPermission;
          const andPerms = Array.isArray(andPermission.and)
            ? andPermission.and
            : [andPermission.and];
          return andPerms.every((p) => this.evaluatePermission(p, resources));
        }

        case "or": {
          const orPermission = permission as OrPermission;
          const orPerms = Array.isArray(orPermission.or)
            ? orPermission.or
            : [orPermission.or];
          return orPerms.some((p) => this.evaluatePermission(p, resources));
        }

        case "not": {
          const notPermission = permission as NotPermission;
          return !this.evaluatePermission(notPermission.not, resources);
        }

        default:
          return false;
      }
    }
    return false;
  }

  /**
   * Helper method to determine the type of permission object
   */
  private getPermissionType(permission: PermissionObject): string | null {
    if ("and" in permission) {
      return "and";
    } else if ("or" in permission) {
      return "or";
    } else if ("not" in permission) {
      return "not";
    }
    return null;
  }
}